// src/components/cards/card-columnist.tsx
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import dynamic from 'next/dynamic';
import Link from 'next/link';

import { sendDataLayerGlobal } from 'src/utils/datalayer/send-datalayer';

import Image from 'src/components/image';

type Props = {
  data: {
    id: number;
    title: string;
    blurb?: string;
    image: string;
    link: string;
    tag?: string;
  };
  showTag?: boolean;
  tagText?: string;
  tagColor?: string;
  eventDataLayer?: any;
  type?: 'FEATURE_IMAGE' | 'COVER_IMAGE' | 'CARD_LARGE' | 'CARD_MEDIUM' | 'CARD_SMALL' | 'CARD_TINY' | 'NEXT_IMAGE' | string;
};

const CardColumnist = ({ data, showTag = true, tagText = 'COLUMNIST', type, tagColor = '#ed1a3b', eventDataLayer = {} }: Props) => {
  if (isEmpty(data)) return null;
  // console.log('data :', data);
  return (
    <CardWrapper $tagColor={tagColor}>
      <Link href={data?.link || '#'}>
        <a onClick={() => sendDataLayerGlobal({ ...eventDataLayer })}>
          <div className='card-inner'>
            <div className='avatar'>
              <Image type={type} image={data?.image} title={data?.title} decorative={true} />
            </div>
            <div className='content'>
              <div className='title'>{data?.title}</div>
              {data?.blurb && <p className='blurb'>{data?.blurb}</p>}
            </div>
            {showTag && <span className='tag'>{tagText}</span>}
          </div>
        </a>
      </Link>
    </CardWrapper>
  );
};

interface StyledProps {
  $tagColor: string;
}

const CardWrapper = styled.div<StyledProps>`
  cursor: pointer;

  a {
    text-decoration: none;
  }

  .card-inner {
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 25px;
    border: 1.5px solid #0b0b0a;
    border-radius: 35px;
    background: white;
    transition: all 0.3s ease;
    position: relative;
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

      .title {
        color: ${({ $tagColor }) => $tagColor};
      }
    }
  }

  .avatar {
    flex-shrink: 0;
    width: 110px;
    height: 110px;
    border-radius: 50%;
    overflow: hidden;
    background: #f0f0f0;

    .resolution-image,
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .content {
    flex: 1;
    min-width: 0;
  }

  .title {
    font-size: 18px;
    font-weight: 700;
    color: #000;
    margin: 0 0 5px;
    line-height: 1.3;
    transition: color 0.3s ease;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .blurb {
    font-size: 14px;
    color: #666;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .tag {
    display: inline-block;
    padding: 6px 12px;
    background-color: #b00022;
    color: white;
    font-size: 14px;
    font-weight: 700;
    border-radius: 4px;
    text-transform: uppercase;
    position: absolute;
    bottom: -14px;
    left: 50%;
    transform: translateX(-50%);
  }

  @media (max-width: 768px) {
    .card-inner {
      padding: 20px 15px;
      gap: 12px;
      border-radius: 25px;
    }

    .avatar {
      width: 60px;
      height: 60px;
    }

    .title {
      font-size: 18px;
    }

    .blurb {
      font-size: 14px;
    }

    .tag {
      font-size: 10px;
      padding: 3px 8px;
      bottom: -10px;
    }
  }
`;

export default CardColumnist;
